import React, { useEffect, useMemo, useState } from 'react';
import ImportValidationStep from '../components/ImportValidationStep';
import './LoadModal.css';

const LoadModal = ({
	show,
	onClose,
	onOptionSelect,
	validationReport,
	onContinueAll,
	onContinueWithoutDefective,
	onRadarlyColumnsConfirm,
	radarlyColumns = [],
	loadMode = 'standard',
}) => {
	const [step, setStep] = useState(1);
	const [selectedOption, setSelectedOption] = useState(null);
	const [activeValidationTab, setActiveValidationTab] = useState('warnings');
	const [selectedRadarlyColumns, setSelectedRadarlyColumns] = useState([]);
	const [columnSearchQuery, setColumnSearchQuery] = useState('');

	const isRadarlyMode = loadMode === 'radarly';
	const validationStep = isRadarlyMode ? 3 : 2;

	useEffect(() => {
		if (!show) {
			setStep(1);
			setSelectedOption(null);
			setActiveValidationTab('warnings');
			setSelectedRadarlyColumns([]);
			setColumnSearchQuery('');
		}
	}, [show, loadMode]);

	useEffect(() => {
		if (isRadarlyMode && radarlyColumns.length > 0) {
			setSelectedRadarlyColumns(radarlyColumns);
		}
	}, [isRadarlyMode, radarlyColumns]);

	useEffect(() => {
		if (!validationReport) return;
		setActiveValidationTab(validationReport.errors?.length > 0 ? 'errors' : 'warnings');
	}, [validationReport]);

	const defectiveCount = validationReport?.defectiveVerbatimIndexes?.length || 0;
	const hasDefectiveVerbatims = defectiveCount > 0;

	const loadTitles = {
		standard: 'Load Verbatims',
		radarly: 'Load Radarly Verbatims',
	};

	const loadDescriptions = {
		standard: 'Choose how the imported verbatims file should be loaded.',
		radarly: 'Choose how the imported Radarly verbatims file should be loaded.',
	};

	const radarlyDescriptions = {
		standard: '',
		radarly: 'Select the columns that should be imported before continuing to validation.',
	};

	const filteredRadarlyColumns = useMemo(() => {
		if (!Array.isArray(radarlyColumns)) return [];

		return radarlyColumns.filter((column) =>
			String(column || '').toLowerCase().includes(columnSearchQuery.toLowerCase())
		);
	}, [radarlyColumns, columnSearchQuery]);

	if (!show) {
		return null;
	}

	const handleActionSelection = (option) => {
		setSelectedOption(option);
		onOptionSelect(option);
		setStep(2);
	};

	const toggleRadarlyColumn = (column) => {
		setSelectedRadarlyColumns((prev) =>
			prev.includes(column)
				? prev.filter((item) => item !== column)
				: [...prev, column]
		);
	};

	const handleRadarlyColumnsValidation = () => {
		if (typeof onRadarlyColumnsConfirm === 'function') {
			onRadarlyColumnsConfirm(selectedRadarlyColumns);
		}

		setStep(validationStep);
	};

	const handleValidationSecondary = () => {
		if (hasDefectiveVerbatims) {
			onContinueWithoutDefective();
			return;
		}

		if (isRadarlyMode && step === validationStep) {
			setStep(2);
			setActiveValidationTab('warnings');
			return;
		}

		setStep(1);
		setSelectedOption(null);
		setActiveValidationTab('warnings');
	};

	const handleValidationPrimary = () => {
		onContinueAll();
	};

	const renderFooter = () => {
		if (step === 1) {
			return (
				<button className="secondary-button" onClick={onClose}>
					Cancel
				</button>
			);
		}

		if (isRadarlyMode && step === 2) {
			return (
				<>
					<button className="secondary-button" onClick={() => setStep(1)}>
						Back
					</button>
					<button
						className="primary-button"
						onClick={handleRadarlyColumnsValidation}
						disabled={selectedRadarlyColumns.length === 0}
					>
						Continue
					</button>
				</>
			);
		}

		if (step === validationStep) {
			return hasDefectiveVerbatims ? (
				<>
					<button className="secondary-button" onClick={handleValidationSecondary}>
						Continue without defective
					</button>
					<button className="primary-button" onClick={handleValidationPrimary}>
						Continue with all
					</button>
				</>
			) : (
				<>
					<button className="secondary-button" onClick={handleValidationSecondary}>
						Back
					</button>
					<button className="primary-button" onClick={handleValidationPrimary}>
						Continue
					</button>
				</>
			);
		}

		return null;
	};

	return (
		<div className="modal-backdrop">
			<div className="modal-content load-modal-content">
				<div className="modal-header">
					<h3>
						{step === 1
							? (loadTitles[loadMode] || loadTitles.standard)
							: isRadarlyMode && step === 2
								? 'Select Radarly columns'
								: 'Import validation'}
					</h3>
					<button className="close-button" onClick={onClose}>
						&times;
					</button>
				</div>

				<div className="modal-body">
					<div className="loadexportmodal-content-wrapper">
						{step === 1 && (
							<>
								<h2>Select Action</h2>
								<p>{loadDescriptions[loadMode] || loadDescriptions.standard}</p>
								<div className="loadexportmodal-action-box-container">
									<div
										className={`loadexportmodal-action-box ${selectedOption === 'append' ? 'active' : ''}`}
										onClick={() => handleActionSelection('append')}
									>
										<h3>Append</h3>
										<p>Adds new verbatims to the existing list. Existing verbatims will be kept.</p>
									</div>
									<div
										className={`loadexportmodal-action-box ${selectedOption === 'overwrite' ? 'active' : ''}`}
										onClick={() => handleActionSelection('overwrite')}
									>
										<h3>Overwrite</h3>
										<p>Replaces the current data with the loaded file. Existing verbatims will be removed.</p>
									</div>
								</div>
							</>
						)}

						{isRadarlyMode && step === 2 && (
							<>
								<h2>Select columns</h2>
								<p>{radarlyDescriptions.radarly}</p>
								<div className="load-modal-column-search">
									<input
										type="text"
										placeholder="Search columns..."
										value={columnSearchQuery}
										onChange={(event) => setColumnSearchQuery(event.target.value)}
									/>
								</div>
								<div className="load-modal-column-counter">
									<strong>{selectedRadarlyColumns.length}</strong> column(s) selected
								</div>
								<div className="load-modal-column-list">
									{filteredRadarlyColumns.length > 0 ? (
										filteredRadarlyColumns.map((column) => (
											<label key={column} className="load-modal-column-item">
												<input
													type="checkbox"
													checked={selectedRadarlyColumns.includes(column)}
													onChange={() => toggleRadarlyColumn(column)}
												/>
												<span>{column}</span>
											</label>
										))
									) : (
										<div className="import-validation-empty-state">
											<div className="import-validation-empty-icon">0</div>
											<div>
												<h4>No column found</h4>
												<p>Try another search term or check the source file structure.</p>
											</div>
										</div>
									)}
								</div>
							</>
						)}

						{step === validationStep && (
							<ImportValidationStep
								validationReport={validationReport}
								activeTab={activeValidationTab}
								onTabChange={setActiveValidationTab}
							/>
						)}
					</div>
				</div>

				<div className="modal-footer">
					{renderFooter()}
				</div>
			</div>
		</div>
	);
};

export default LoadModal;
